/** Copyright © 2025 Qcells. All rights reserved.
This software is proprietary and confidential. Unauthorized use,
duplication, or distribution of software is strictly prohibited.
*/

import type { AxiosInstance } from 'axios';

import { Service } from '@hems/service/src/api/Service';
import { apiWrapper } from '@hems/service/src/util/apiHelper';

import type { CommonResponseWrapper, ListResponse } from 'hems';

import type { SmartModuleACInfo, SmartModuleDayPwData, SmartModule15MinPwData } from 'hems/device/smartmodule';
import type { CanvasJsonData, MicroInverterActivityQueryParam, MicroInverterActivityResponse } from 'hems/mapper';

export type SmartModulePVEnergyParams = {
  from?: string;
  to?: string;
  timezoneId?: string;
};

export type SmartModuleObjectSaveParams = {
  /** 저장 파일 경로 */
  drawingPath: string;
  /** 저장 파일 용량 */
  fileSize: number;
  /** 저장 파일 명 */
  newFileName: string;
  /** 원본 파일 명 */
  orgFileName: string;
};

export default class SmartModuleService extends Service {
  constructor(axios: AxiosInstance) {
    super(axios);

    /** @FIXME API에 제거 요청. 수정해주면 이후 아래 로직 제거 필요 */
    this.setContentTypeHeaderInterceptor();
  }

  /** 도면 파일 가져오기 */
  getDrawingFileBySiteId = async (siteId: number): Promise<CanvasJsonData> => {
    return await this.get<CanvasJsonData>(apiWrapper.managerApi(`/devices/profiles/ac/drawing/${siteId}/sites`));
  };

  /** 도면 파일 추가 및 업데이트 */
  saveDrawingFileBySiteId = async (siteId: number, data: FormData): Promise<SmartModuleObjectSaveParams> => {
    const response = await this.post<CommonResponseWrapper<SmartModuleObjectSaveParams>>(
      apiWrapper.managerApi(`/devices/profiles/ac/drawing/${siteId}/sites`),
      data,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  };

  /** 모듈 정보 조회 */
  getModulesInfoBySiteId = async (siteId: number): Promise<SmartModuleACInfo[]> => {
    const { data } = await this.get<CommonResponseWrapper<{ result: SmartModuleACInfo[] }>>(
      apiWrapper.managerApi(`/devices/telemetries/ac/${siteId}/mi-profiles`)
    );

    return data.result;
  };

  getEnergyMonitoringInfoBySiteId = async (
    siteId: number,
    interval: string,
    params: SmartModulePVEnergyParams
  ): Promise<SmartModuleDayPwData[]> => {
    const { data } = await this.get<CommonResponseWrapper<{ result: SmartModuleDayPwData[] }>>(
      apiWrapper.managerApi(`/devices/telemetries/ac/${siteId}/energies/${interval}`),
      params
    );

    return data.result;
  };

  /** 15분 단위 PV 전력 정보 조회 */
  get15MinPvPowerInfoACBySiteId = async (
    siteId: number,
    params: SmartModulePVEnergyParams
  ): Promise<SmartModule15MinPwData[]> => {
    const { data } = await this.get<CommonResponseWrapper<{ result: SmartModule15MinPwData[] }>>(
      apiWrapper.managerApi(`/devices/telemetries/ac/${siteId}/powers`),
      params
    );

    return data.result;
  };

  /** MI 이력 정보 조회 */
  getMicroInverterActivities = async (
    siteId: number,
    deviceId: string,
    MicroInverterActivityParam?: MicroInverterActivityQueryParam
  ): Promise<MicroInverterActivityResponse[]> => {
    const { data } = await this.get<CommonResponseWrapper<ListResponse<MicroInverterActivityResponse[]>>>(
      apiWrapper.managerApi(`/sites/${siteId}/devices/${deviceId}/micro-inverters/activities`),
      MicroInverterActivityParam
    );

    return data.body;
  };
}

export { SmartModuleService };
